import type { Address } from 'app-components/flux/addresses/addressTypes';
import { type FormBuilder, ValidationTrigger } from 'app-components/utils/createForm/formBuilder';

interface AddressState {
  address: Address;
  addressEnabled: boolean;
}

export function addAddressFields<
  State extends AddressState,
  StoreState,
  Input,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
>(
  formBuilder: FormBuilder<
    State,
    StoreState,
    Input,
    SingleFieldNames,
    SingleFields,
    MultiFieldNames,
    MultiFields,
    ActionNames,
    Actions
  >,
) {
  return formBuilder
    .addField('line1', {
      selector: (state) => state.address.line1 ?? '',
      onValueChange(draft, value) {
        draft.address.line1 = value;
      },
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          isValid: (state) => !state.addressEnabled || !!state.address.line1,
          error: 'Required',
        },
      },
    })
    .addField('line2', {
      selector: (state) => state.address.line2 ?? '',
      onValueChange(draft, value) {
        draft.address.line2 = value;
      },
    })
    .addField('state', {
      selector: (state) => state.address.state ?? '',
      onValueChange(draft, value) {
        draft.address.state = value;
      },
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          isValid: (state) => !state.addressEnabled || !!state.address.state,
          error: 'Required',
        },
      },
    })
    .addField('zipCode', {
      selector: (state) => state.address.zipCode ?? '',
      onValueChange(draft, value) {
        draft.address.zipCode = value;
      },
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          isValid: (state) => !state.addressEnabled || !!state.address.zipCode,
          error: 'Required',
        },
      },
    })
    .addField('city', {
      selector: (state) => state.address.city ?? '',
      onValueChange(draft, value) {
        draft.address.city = value;
      },
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          isValid: (state) => !state.addressEnabled || !!state.address.city,
          error: 'Required',
        },
      },
    })
    .addField('addressEnabled', {
      selector: (state) => state.addressEnabled,
      onValueChange(draft, value) {
        draft.addressEnabled = value;
      },
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          // checks if any address field is in error
          isValid: (state) =>
            !state.addressEnabled ||
            !!(state.address.line1 && state.address.state && state.address.city && state.address.zipCode),
          error: 'Required',
        },
      },
    });
}
